<template>
  <div class="flex flex-column flex-1 pl-2 pr-2" style="height: 90%">
    <DataTable :value="data" class="tabla">
      <Column field="createdAt" header="Fecha">
        <template #body="{ data }">
          <span class="font-semibold" style="font-size: 15px">
            Hace{{ fromTime(data.createdAt) }}
          </span>
          <br />
          <span>{{ dayjs(data.createdAt).format('YYYY-MM-D HH:mm:ss') }}</span>
        </template>
      </Column>
      <Column field="context" header="Contexto">
        <template #body="{ data, field }">
          <span v-if="data[field]" class="font-medium">{{ data[field] }}</span>
          <span v-else>Contexto no disponible</span>
        </template>
      </Column>

      <Column field="userName" header="Usuario">
        <template #body="{ data, field }">
          <span v-if="data[field]" class="font-semibold">{{
            data[field]
          }}</span>
          <span v-else>Usuario no disponible</span>
        </template>
      </Column>
      <Column field="modelName" header="Entidad"></Column>
      <Column field="recordId" header="Id del registro"></Column>
      <Column field="operation" header="Acción"></Column>
      <Column header="Cambios">
        <template #body="{ data }">
          <table>
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="change in formatAuditChanges(
                  data.previousData,
                  data.newData
                )"
                :key="change.field"
              >
                <tr>
                  <td class="p-2 font-semibold">{{ change.field }}</td>
                  <td
                    class="p-2 font-medium text-600 flex flex-column"
                    v-html="convertToString(change.oldValue)"
                  ></td>
                  <td class="p-2 font-medium text-600">→</td>
                  <td
                    class="p-2 font-medium text-600 flex flex-column"
                    v-html="convertToString(change.newValue)"
                  ></td>
                </tr>
              </template>
            </tbody>
          </table>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
<script lang="ts" setup>
  import dayjs from 'dayjs'
  import fromTime from '@/shared/utils/fromTime'
  import utc from 'dayjs/plugin/utc'
  import DataTable from 'primevue/datatable'
  import Column from 'primevue/column'
  import useLoading from '@/store/load'
  import Audit from '../models/audit'
  dayjs.extend(utc)

  const loadingStore = useLoading()

  const props = defineProps<{
    data: Audit[]
    onClose: () => void
  }>()

  const formatAuditChanges = (prevData: any, newData: any, parentKey = '') => {
    const response: Array<{ field: string; oldValue: any; newValue: any }> = []

    const isObject = (value: any) =>
      value && typeof value === 'object' && !Array.isArray(value)

    const isDifferent = (a: any, b: any) => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return (
          a.length !== b.length ||
          a.some(
            (item, index) => JSON.stringify(item) !== JSON.stringify(b[index])
          )
        )
      } else if (isObject(a) && isObject(b)) {
        return JSON.stringify(a) !== JSON.stringify(b)
      }
      return a !== b
    }

    const traverse = (prev: any, current: any, keyPrefix: string) => {
      const allKeys = new Set([
        ...Object.keys(prev || {}),
        ...Object.keys(current || {}),
      ])

      allKeys.forEach((key) => {
        const fullKey = keyPrefix ? `${keyPrefix}.${key}` : key

        const oldValue = prev?.[key]
        const newValue = current?.[key]

        if (isObject(oldValue) || isObject(newValue)) {
          if (isDifferent(oldValue, newValue)) {
            traverse(oldValue || {}, newValue || {}, fullKey)
          }
        } else if (Array.isArray(oldValue) || Array.isArray(newValue)) {
          if (isDifferent(oldValue, newValue)) {
            response.push({
              field: fullKey,
              oldValue: oldValue || [],
              newValue: newValue || [],
            })
          }
        } else if (oldValue !== newValue) {
          response.push({
            field: fullKey,
            oldValue,
            newValue,
          })
        }
      })
    }

    traverse(prevData || {}, newData || {}, parentKey)
    return response
  }

  const convertToString = (record: any): string => {
    if (Array.isArray(record)) {
      if (record.every((item) => typeof item === 'object')) {
        return record
          .map((item, index) => {
            const formattedItem = Object.entries(item)
              .map(([key, value]) => `<strong>${key}</strong>: ${value}`)
              .join('<br>')
            return `<div>Item ${index + 1}:<br>${formattedItem}</div>`
          })
          .join('<hr>')
      }
      return record.join(', ')
    } else if (typeof record === 'object' && record !== null) {
      return Object.entries(record)
        .map(
          ([key, value]) => `<strong>${key}</strong>: ${convertToString(value)}`
        )
        .join('<br>')
    } else if (record === null || record === undefined) {
      return ''
    } else {
      return String(record)
    }
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: none;
    border-radius: 16px;
    border: 3px solid #e2e8f0;
    border-radius: 9px;
    width: 98%;
    max-height: 100%;
    overflow-y: scroll;
  }

  :deep(.p-column-title) {
    font-size: 14px;
    color: #3b82f6;
  }
</style>
